import heroImage from 'assets/img/sign-up/hero.png';
import { Field, Form, Formik } from 'formik';
import log from 'loglevel';
import React from 'react';
import { toast } from 'sonner';
import { IAgentLandingAgentData, IAgentLandingMarketStats } from 'types/agent-landing-page';
import * as Yup from 'yup';

import DateRangeOutlinedIcon from '@material-ui/icons/DateRangeOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ShowChartOutlinedIcon from '@material-ui/icons/ShowChartOutlined';

import { Eyebrow, normalizeAgents } from 'components/AgentLanding/AgentLandingPrimitives';
import { trackCustomEvent } from 'components/MetaPixelWrapper';

import { getAgentLandingMarketStats, submitAgentLandingZipCodeForm } from 'utils/api';

type Props = {
  agentLandingPageToken: string;
  agentData: IAgentLandingAgentData;
  onLoading: (loading: boolean, zipCode?: string) => void;
  onResult: (data: IAgentLandingMarketStats) => void;
  showBenefits: boolean;
  showPreview: boolean;
};

const DigitalAdZipCodeForm = ({
  agentLandingPageToken, agentData, onLoading, onResult, showBenefits, showPreview,
}: Props) => {
  const agents = normalizeAgents(agentData);
  return (
    <>
      <section className="al-ad-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="al-shell al-ad-grid">
          <div className="al-ad-copy">
            <Eyebrow light>Free neighborhood snapshot</Eyebrow>
            <h1>
              What is your local market doing
              {' '}
              <em>right now?</em>
            </h1>
            <p>See recent sale prices, buyer activity, and the pace of your neighborhood market in one concise report.</p>
            <Formik
              initialValues={{ zipCode: '' }}
              validationSchema={Yup.object({ zipCode: Yup.string().matches(/^\d{5}$/, 'Enter a valid 5-digit ZIP code').required('ZIP code is required') })}
              onSubmit={async ({ zipCode }, helpers) => {
                onLoading(true, zipCode);
                try {
                  trackCustomEvent('ZipCodeFormSubmitted');
                  const [marketStats] = await Promise.all([
                    getAgentLandingMarketStats(agentLandingPageToken, zipCode),
                    submitAgentLandingZipCodeForm(agentLandingPageToken, zipCode).catch((error) => {
                      log.warn('Could not track agent landing ZIP code form submission', error);
                    }),
                  ]);
                  onResult(marketStats);
                } catch (error: any) {
                  toast.error(error.response?.data?.error || 'Market data could not be loaded. Please try another ZIP code.');
                  onLoading(false);
                } finally {
                  helpers.setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                touched,
                isSubmitting,
                setFieldValue,
              }) => (
                <Form className="al-zip-form" noValidate>
                  <span id="agent-landing-zip-label" className="al-zip-label">Enter your property ZIP code</span>
                  <div className="al-zip-control">
                    <Field
                      id="agent-landing-zip"
                      name="zipCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                      autoComplete="postal-code"
                      placeholder="e.g. 91202"
                      aria-labelledby="agent-landing-zip-label"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const digitsOnly = event.currentTarget.value.replace(/\D/g, '').slice(0, 5);
                        setFieldValue('zipCode', digitsOnly);
                      }}
                    />
                    <button type="submit" disabled={isSubmitting}>See my market profile →</button>
                  </div>
                  {touched.zipCode && errors.zipCode && <div className="al-zip-error" role="alert">{errors.zipCode}</div>}
                  <small className="al-zip-privacy">
                    <LockOutlinedIcon />
                    {' '}
                    Free, private, and no obligation.
                  </small>
                </Form>
              )}
            </Formik>
            <div className="al-ad-proof">
              <div className="al-ad-proof-portraits">
                {agents.map((agent) => (agent.pictureUrl
                  ? <img key={agent.firstName} src={agent.pictureUrl} alt={`Portrait of ${agent.firstName} ${agent.lastName || ''}`.trim()} />
                  : (
                    <span key={agent.firstName} className="al-agent-fallback">
                      {agent.firstName.charAt(0)}
                      {agent.lastName?.charAt(0)}
                    </span>
                  )))}
              </div>
              <span>
                <strong>
                  {`Prepared by ${agents.map((agent) => `${agent.firstName} ${agent.lastName || ''}`.trim()).join(' & ')}`}
                </strong>
                <small>Local expertise, backed by current data</small>
              </span>
            </div>
          </div>
          {showPreview && (
            <aside className="al-market-preview" aria-label="Example market report preview">
              <header>
                <span>SAMPLE MARKET PULSE</span>
                {/* <small>Updated this week</small> */}
              </header>
              <span>Median sale price</span>
              <strong>$1.42M</strong>
              <small className="al-market-preview-trend">
                <b>↑ 6.3%</b>
                {' '}
                vs. prior 30 days
              </small>
              <div className="al-preview-stats">
                <div>
                  <strong>24</strong>
                  <span>Days on market</span>
                </div>
                <div>
                  <strong>18</strong>
                  <span>Recent sales</span>
                </div>
                <div>
                  <strong>97.8%</strong>
                  <span>Sale-to-list</span>
                </div>
              </div>
              <svg className="al-preview-spark" viewBox="0 0 420 120" aria-label="Rising market trend">
                <path d="M4 100 C55 95,70 65,120 73 S190 104,230 62 S310 42,330 48 S380 17,416 23" fill="none" stroke="#F26430" strokeWidth="5" />
                <path d="M4 100 C55 95,70 65,120 73 S190 104,230 62 S310 42,330 48 S380 17,416 23 L416 120 L4 120Z" fill="rgba(242,100,48,.12)" />
              </svg>
              <p>
                <strong>Sample preview</strong>
                {' '}
                · Your report is personalized to your ZIP code.
              </p>
            </aside>
          )}
        </div>
      </section>
      {showBenefits && (
        <section className="al-ad-benefits">
          <div className="al-shell">
            <Eyebrow>Inside your snapshot</Eyebrow>
            <div className="al-benefit-grid">
              <article>
                <HomeOutlinedIcon />
                <h3>Recent nearby sales</h3>
                <p>See what comparable homes have actually sold for.</p>
              </article>
              <article>
                <DateRangeOutlinedIcon />
                <h3>Market pace</h3>
                <p>Understand how quickly homes are moving in your area.</p>
              </article>
              <article>
                <ShowChartOutlinedIcon />
                <h3>Price direction</h3>
                <p>Track the current trend without wading through raw data.</p>
              </article>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default DigitalAdZipCodeForm;
